import { brand, shadows } from '@alison/ui';
import Box from '@mui/material/Box';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import {
  type ComponentType,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  MidFlightActiveIcon,
  MidFlightIcon,
  PostFlightActiveIcon,
  PostFlightIcon,
  PreFlightActiveIcon,
  PreFlightIcon,
} from './stageIcons';

const STAGES = ['all', 'pre', 'mid', 'post'] as const;

const SEGMENTS: {
  id: Stage;
  label: string;
  icon?: ComponentType<SvgIconProps>;
  activeIcon?: ComponentType<SvgIconProps>;
}[] = [
  { id: 'all', label: 'All stages' },
  { id: 'pre', label: 'Pre-flight', icon: PreFlightIcon, activeIcon: PreFlightActiveIcon },
  { id: 'mid', label: 'Mid-flight', icon: MidFlightIcon, activeIcon: MidFlightActiveIcon },
  { id: 'post', label: 'Post-flight', icon: PostFlightIcon, activeIcon: PostFlightActiveIcon },
];

const PILL_GLIDE_MS = 320;
const TAKE_OFF_MS = 560;
const IDLE_MS = 30_000;
const WAVE_MAX_RUNS = 3;
const WAVE_STAGGER_MS = 140;
const WAVE_MS = 1100;
const WAVE_ICON_MS = WAVE_MS - WAVE_STAGGER_MS * 2;
const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel'] as const;

export type Stage = (typeof STAGES)[number];

interface StageToggleProps {
  value: Stage;
  onChange: (stage: Stage) => void;
}

interface PillRect {
  left: number;
  width: number;
}

function useIdleWave(): boolean {
  const [waving, setWaving] = useState(false);

  useEffect(() => {
    let runs = 0;
    let idleTimer: ReturnType<typeof setTimeout> | undefined;
    let endTimer: ReturnType<typeof setTimeout> | undefined;

    const stop = (): void => {
      clearTimeout(idleTimer);
      clearTimeout(endTimer);
      setWaving(false);
    };
    const arm = (): void => {
      stop();
      if (runs >= WAVE_MAX_RUNS || document.hidden) return;
      idleTimer = setTimeout(() => {
        runs += 1;
        setWaving(true);
        endTimer = setTimeout(arm, WAVE_MS);
      }, IDLE_MS);
    };

    for (const event of ACTIVITY_EVENTS) document.addEventListener(event, arm, { passive: true });
    document.addEventListener('visibilitychange', arm);
    arm();
    return () => {
      stop();
      for (const event of ACTIVITY_EVENTS) document.removeEventListener(event, arm);
      document.removeEventListener('visibilitychange', arm);
    };
  }, []);

  return waving;
}

export function StageToggle({ value, onChange }: StageToggleProps): ReactNode {
  const groupRef = useRef<HTMLDivElement | null>(null);
  const segmentRefs = useRef<Partial<Record<Stage, HTMLButtonElement | null>>>({});
  const [pill, setPill] = useState<PillRect | null>(null);
  const [wavePath, setWavePath] = useState<PillRect | null>(null);
  const [flying, setFlying] = useState<Stage | null>(null);
  const waving = useIdleWave();

  useLayoutEffect(() => {
    const selected = segmentRefs.current[value];
    if (selected) setPill({ left: selected.offsetLeft, width: selected.offsetWidth });
  }, [value]);

  useLayoutEffect(() => {
    const group = groupRef.current?.getBoundingClientRect();
    const first = segmentRefs.current.pre?.querySelector('svg')?.getBoundingClientRect();
    const last = segmentRefs.current.post?.querySelector('svg')?.getBoundingClientRect();
    if (!waving || !group || !first || !last) return;
    setWavePath({ left: first.left - group.left, width: last.right - first.left });
  }, [waving]);

  const select = (stage: Stage): void => {
    if (stage === value) return;
    setFlying(stage);
    onChange(stage);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (step === 0) return;
    event.preventDefault();
    const next = STAGES[(STAGES.indexOf(value) + step + STAGES.length) % STAGES.length];
    if (next === undefined) return;
    select(next);
    segmentRefs.current[next]?.focus();
  };

  return (
    <Box
      ref={groupRef}
      role="radiogroup"
      aria-label="Stage"
      onKeyDown={onKeyDown}
      sx={(theme) => ({
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '2px',
        p: '3px',
        borderRadius: 1,
        bgcolor: 'background.platform',
        '@keyframes stageTakeOff': {
          '0%': { transform: 'none' },
          '30%': { transform: 'translate(2px, -3px) rotate(-8deg)' },
          '60%': { transform: 'translate(3px, -3px)' },
          '100%': { transform: 'none' },
        },
        '@keyframes stageWave': {
          '35%': { transform: 'translateY(-3px)', color: brand.violet },
          '65%': { transform: 'translateY(-3px)', color: brand.blue },
        },
        '@keyframes stageWavePath': {
          '0%': { clipPath: 'inset(0 100% 0 0)', opacity: 1 },
          '60%': { clipPath: 'inset(0 0 0 0)', opacity: 1 },
          '80%': { opacity: 1 },
          '100%': { clipPath: 'inset(0 0 0 0)', opacity: 0 },
        },
        '@keyframes stageContrail': {
          '0%': { opacity: 0, transform: 'translateX(4px) scaleX(0.4)' },
          '40%': { opacity: 0.7, transform: 'none' },
          '100%': { opacity: 0, transform: 'translateX(-4px) scaleX(1.2)' },
        },
        '& .stage-wave-path, & .stage-contrail': {
          position: 'absolute',
          height: 2,
          borderRadius: 1,
          pointerEvents: 'none',
        },
        '& .stage-wave-path': {
          bottom: 5,
          background: `linear-gradient(90deg, ${brand.violet}, ${brand.blue})`,
          animation: `stageWavePath ${WAVE_MS}ms linear both`,
        },
        '& .stage-contrail': {
          top: '50%',
          mt: '-1px',
          left: 4,
          width: 10,
          transformOrigin: 'right center',
          background: `linear-gradient(90deg, transparent, ${brand.blue})`,
          animation: `stageContrail ${TAKE_OFF_MS}ms ease-out both`,
        },
        '& .stage-pill': {
          position: 'absolute',
          top: 3,
          bottom: 3,
          borderRadius: 0.8,
          bgcolor: 'background.paper',
          boxShadow: shadows.chrome,
          transition: `left ${PILL_GLIDE_MS}ms ease, width ${PILL_GLIDE_MS}ms ease`,
        },
        '& .stage-segment': {
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: '6px',
          border: 0,
          borderRadius: 0.8,
          bgcolor: 'transparent',
          cursor: 'pointer',
          fontSize: 10,
          fontWeight: 500,
          color: 'text.muted',
          whiteSpace: 'nowrap',
          transition: 'color 200ms ease',
          '&[aria-checked="true"]': { fontWeight: 600, color: 'primary.main' },
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 1,
          },
        },
        '& .stage-icon': {
          fontSize: 16,
          transition: 'transform 150ms ease',
        },
        '& .stage-segment:not([aria-checked="true"]):hover .stage-icon': {
          transform: 'translateX(2px)',
        },
        '& .stage-icon.is-flying': {
          animation: `stageTakeOff ${TAKE_OFF_MS}ms ease-in-out`,
        },
        '& .stage-icon.is-waving': {
          animation: `stageWave ${WAVE_ICON_MS}ms ease-in-out both`,
        },
        '@media (prefers-reduced-motion: reduce)': {
          '& .stage-pill, & .stage-icon': { transition: 'none', animation: 'none' },
          '& .stage-wave-path, & .stage-contrail': { display: 'none' },
        },
      })}
    >
      {pill ? <Box className="stage-pill" style={pill} aria-hidden /> : null}
      {waving && wavePath ? <Box className="stage-wave-path" style={wavePath} aria-hidden /> : null}
      {SEGMENTS.map(({ id, label, icon, activeIcon }, index) => {
        const selected = id === value;
        const Icon = selected ? activeIcon : icon;
        return (
          <Box
            key={id}
            component="button"
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            className="stage-segment"
            ref={(node: HTMLButtonElement | null) => {
              segmentRefs.current[id] = node;
            }}
            onClick={() => select(id)}
          >
            {flying === 'mid' && id === 'mid' ? (
              <Box className="stage-contrail" aria-hidden />
            ) : null}
            {Icon ? (
              <Icon
                className={`stage-icon${flying === id ? ' is-flying' : ''}${waving ? ' is-waving' : ''}`}
                style={
                  waving ? { animationDelay: `${(index - 1) * WAVE_STAGGER_MS}ms` } : undefined
                }
                onAnimationEnd={() => setFlying(null)}
              />
            ) : null}
            {label}
          </Box>
        );
      })}
    </Box>
  );
}
